import type { TranslationDictionary } from "./types";

export const es: TranslationDictionary = {
  // ── EmailGate ──
  "page.title": "Tu Propuesta | Woba",
  "emailGate.placeholder": "tu@correo.com",
  "emailGate.invalidEmail": "Por favor, ingresa un correo válido",
  "emailGate.blockedEmail":
    "Por favor, ingresa un correo corporativo o personal válido",
  "emailGate.loading": "Cargando...",
  "emailGate.notFound": "Propuesta no encontrada",
  "emailGate.loadError":
    "No fue posible cargar la propuesta. Verifica el enlace o intenta de nuevo.",
  "emailGate.retry": "Intentar de Nuevo",
  "emailGate.proposalLoadError":
    "Error al cargar la propuesta. Intenta de nuevo.",

  // ── ProposalCoverScreen (pantalla 1) ──
  "cover.ctaLabel": "Ver mi propuesta",
  "cover.talkToConsultant": "Hablar con {{name}}",
  "cover.scheduleNote.bold": "Propuesta válida hasta {{date}}.",
  "cover.scheduleNote.rest":
    "Asegura tu oficina: agenda una visita presencial o por video.",
  "cover.toggle.traditional": "Alquiler tradicional",
  "cover.toggle.woba": "Woba",

  // ── ProposalContent ──
  "proposal.notFoundOrExpired": "Propuesta no encontrada o expirada.",

  // ── Stepper / navegación por etapas ──
  "steps.comparison": "Ventaja Woba",
  "steps.spaces.singular": "Espacio Seleccionado",
  "steps.spaces.plural": "Espacios Seleccionados",
  "steps.scheduling": "Agendar Visita",
  "steps.proposal": "Hacer Propuesta",
  "steps.briefing": "Otras opciones",
  "stepper.next": "Próximo: {{name}}",
  "nav.back": "Volver",
  "nav.continue": "Continuar",
  "nav.requestVisit": "Solicitar visita",
  "nav.backToRoomsList": "Volver a la lista de oficinas",

  // ── Hero copy (used by ProposalCoverScreen) ──
  "hero.subtitle":
    "Abandona las complejidades del modelo tradicional y centraliza todo en un único contrato inteligente.",
  "hero.emailPrompt":
    "Tu propuesta está lista, ingresa tu correo para acceder.",

  // ── Chat ──
  "chat.contextLabel": "Respondiendo dudas sobre",
  "chat.placeholder": "Pregunta sobre este espacio...",
  "chat.welcomeLine1":
    "¡Hola! 👋 Estoy aquí para resolver tus dudas sobre **{{spaceName}}**.",
  "chat.welcomePrice": "Este espacio cuesta **R$ {{price}}/posición/mes**.",
  "chat.welcomeAskAnything":
    "Pregunta lo que quieras sobre este espacio — si no puedo responder al instante, consultaremos al partner y te avisamos.",
  "chat.errorMessage":
    "Lo sentimos, ocurrió un error al procesar tu mensaje. Intenta de nuevo.",
  "chat.dividerNowAbout": "Ahora sobre: {{spaceName}}",
  "chat.sendAriaLabel": "Enviar",
  "chat.chip.monthlyValue": "¿Cuál es el valor mensual?",
  "chat.chip.parking": "¿Tiene estacionamiento?",
  "chat.chip.internet": "¿Cuál es la velocidad del internet?",
  "chat.chip.meetingRooms": "¿Incluye salas de reunión?",
  "chat.chip.credits": "¿Cómo funcionan los créditos Woba?",
  "chat.chip.contract": "¿Cuál es la flexibilidad del contrato?",
  "chat.chip.scheduleVisit": "Quiero agendar una visita",

  // ── SpacesSection ──
  "spaces.selectedSpaces": "Espacios Seleccionados",
  "spaces.optionsFor": "Opciones para {{companyName}}",
  "spaces.optionsForDefault": "Opciones para tu empresa",
  "spaces.bestOfficesInCity":
    "Seleccionamos las mejores oficinas en {{city}} para tu operación",
  "spaces.bestOptions": "Seleccionamos las mejores opciones para tu operación",
  "spaces.availableOptions": "Opciones Disponibles",
  "spaces.recommended": "★ Recomendado",
  "spaces.combo": "Conjunto",
  "spaces.pos": "pos",
  "spaces.scopeOffice": "Oficina Privada",
  "spaces.scopeSpace": "Área común",
  "spaces.detail.roomId": "ID de la sala",
  "spaces.detail.spaceId": "ID del espacio",
  "spaces.detail.back": "Volver",
  "spaces.detail.close": "Cerrar",
  "spaces.detail.expandPhotos": "Ver todas las fotos",
  "spaces.detail.officePhotosHeading": "Fotos de {{label}}",
  "spaces.detail.spacePhotosHeading": "Fotos del Área común",
  "spaces.detail.positionsHeader": "Posiciones",
  "spaces.detail.sizeLabel": "Tamaño",
  "spaces.detail.pricePerPositionLabel": "Val. mensual / posición",
  "spaces.detail.summary":
    "Oficina privada para operar con {{capacity}} posiciones y {{size}} m², en el barrio {{neighborhood}} en {{city}}.",
  "spaces.detail.summaryNoSize":
    "Oficina privada para operar con {{capacity}} posiciones, en el barrio {{neighborhood}} en {{city}}.",
  "spaces.detail.summaryNoNeighborhood":
    "Oficina privada para operar con {{capacity}} posiciones y {{size}} m², en {{city}}.",
  "spaces.detail.summaryNoSizeNoNeighborhood":
    "Oficina privada para operar con {{capacity}} posiciones, en {{city}}.",
  "spaces.detail.summaryComboSuffix":
    " Esta oficina forma parte de un conjunto de {{total}} oficinas reunidas para atender tu solicitud completa.",
  "spaces.detail.spaceSummary":
    "El área común del espacio cuenta con {{amenities}} comodidades disponibles.",
  "spaces.detail.aboutOfficeTitle": "Sobre la oficina",
  "spaces.detail.aboutSpaceTitle": "Sobre el área común",
  // Conformidad (PED-12757): documentos del edificio, nunca de la sala — por
  // eso el tooltip nombra al espacio como sujeto. Solo estados positivos.
  "spaces.detail.complianceTitle": "Conformidad",
  "spaces.detail.complianceAvcb": "Certificado de bomberos (AVCB)",
  "spaces.detail.complianceAvcbInfo":
    "Inspección de bomberos que certifica la seguridad del edificio contra incendios.",
  "spaces.detail.compliancePermit": "Licencia de funcionamiento",
  "spaces.detail.compliancePermitInfo":
    "Licencia municipal que autoriza el funcionamiento del edificio en esta dirección.",
  "spaces.detail.amenitiesTitle": "Amenidades",
  "spaces.detail.amenitiesSubtitleOffice":
    "Qué está disponible dentro de la oficina privada",
  "spaces.detail.amenitiesSubtitleSpace":
    "Qué está disponible en el área común",
  "spaces.detail.locationTitle": "Ubicación",
  "spaces.tabNavLabel": "Navegación del espacio",
  "spaces.fullscreenTitle": "Ubicación de los Espacios",
  "spaces.mapButton": "Mostrar mapa",
  "spaces.map.pinOffices": "{{count}} oficinas",
  "spaces.map.showList": "Mostrar lista",
  "spaces.viewMode.cards": "Ver en tarjetas",
  "spaces.viewMode.list": "Ver en lista",
  "spaces.table.office": "Oficina",
  "spaces.table.monthlyValue": "Valor/mes",
  "spaces.table.pricePerPosition": "Costo/posición",
  "spaces.table.credits": "Pases diarios/mes",
  "spaces.table.creditBonusShort": "Gana {{credits}}",
  "spaces.mapLayers.button": "Puntos de interés",
  "spaces.mapLayers.viewLabel": "Visualización",
  "spaces.mapLayers.view3d": "3D",
  "spaces.mapLayers.view2d": "2D",
  "spaces.mapLayers.poiLabel": "Puntos de interés",
  "spaces.card.sqm": "{{value}} m²",
  "spaces.card.monthlyValue": "Valor mensual",
  "spaces.card.creditBonus": "Gana {{credits}} pases diarios/mes",
  "spaces.card.wantToVisit": "Seleccionar",
  "spaces.card.selected": "Seleccionado",
  "spaces.card.visitRequested": "Visita solicitada",
  "spaces.card.proposalSent": "Propuesta enviada",
  "spaces.card.spaceHasAvcb": "Espacio con AVCB",
  "spaces.card.spaceHasPermit": "Espacio con licencia de funcionamiento",
  "spaces.card.complianceTooltip":
    "El espacio donde está esta sala posee el documento",
  "spaces.card.viewDetails": "Ver detalles",
  "spaces.card.spacePhoto": "Área común",
  "spaces.card.comboRoomLabel": "Oficina {{number}}",
  "spaces.card.comboOfficesCount": "{{count}} oficinas",
  "spaces.card.detailsTitle": "Detalles del espacio",
  "spaces.header.selectToVisit": "Elige el espacio ideal",
  "spaces.header.title.singular": "1 Oficina Privada",
  "spaces.header.title.plural": "{{count}} Oficinas Privadas",
  "spaces.header.title.filteredSingular":
    "{{count}} de {{total}} Oficina Privada",
  "spaces.header.title.filteredPlural":
    "{{count}} de {{total}} Oficinas Privadas",
  "spaces.header.lead":
    "Cada opción pasó por la curaduría de Woba. Ninguna por casualidad.",
  "spaces.header.briefing.singular":
    "Elegimos una Oficina Privada para tu pedido: {{details}}.",
  "spaces.header.briefing.plural":
    "Elegimos algunas Oficinas Privadas para tu pedido: {{details}}.",
  "spaces.header.briefing.inCity": "en {{city}}",
  "spaces.header.briefing.positionsExact": "{{count}} posiciones",
  "spaces.header.briefing.contract": "contrato de {{months}} meses",
  "spaces.filters.sortBy": "Ordenar por",
  "spaces.filters.sortRecommended": "Recomendado",
  "spaces.filters.sortCheapest": "Más barato",
  "spaces.filters.sortMostPositions": "Más posiciones",
  "spaces.filters.capacity": "Capacidad",
  "spaces.filters.priceRange": "Rango de precio",
  "spaces.filters.size": "Tamaño",
  "spaces.filters.capacityUpTo": "Hasta {{max}} posiciones",
  "spaces.filters.capacityBetween": "{{min}} a {{max}} posiciones",
  "spaces.filters.capacityAbove": "Más de {{min}} posiciones",
  "spaces.filters.priceUpTo": "Hasta R$ {{max}}",
  "spaces.filters.priceBetween": "R$ {{min}} a R$ {{max}}",
  "spaces.filters.priceAbove": "Más de R$ {{min}}",
  "spaces.filters.sizeUpTo": "Hasta {{max}} m²",
  "spaces.filters.sizeBetween": "{{min}} a {{max}} m²",
  "spaces.filters.sizeAbove": "Más de {{min}} m²",
  "spaces.filters.noResults": "Ningún espacio coincide con estos filtros.",
  "spaces.selectedBar.hintEmpty": "Selecciona un espacio para continuar",
  "spaces.selectedBar.hintSelected": "Elige cómo deseas continuar",

  // ── PhotosTab ──
  "photos.prevPhoto": "Foto anterior",
  "photos.nextPhoto": "Siguiente foto",
  "photos.noImage": "Sin imagen disponible",
  "photos.didYouLike": "¿Te gustó?",
  "photos.liked": "Me gustó",
  "photos.disliked": "No me gustó",
  "photos.positions": "posiciones",
  "photos.perPosition": "R$ {{price}} / posición",
  "photos.priceOnRequest": "Valor bajo consulta",
  "photos.licenseFee": "License Fee R$ {{price}}",
  "photos.licenseFeeIncluded": "License Fee incluido",

  // ── AmenitiesTab ──
  "amenities.notAvailable": "Información de amenidades no disponible.",
  "amenities.showOthers": "Otras ({{count}})",
  "amenities.showLess": "Ver menos",

  // ── RoomEmbeddedMap (distance calculator) ──
  "map.calculateDistance": "Calcular distancia entre el espacio y...",
  "map.placeholder": "Ej: Av. Paulista, 1000",
  "map.searching": "Buscando...",
  "map.calculate": "Calcular",
  "map.distancesFrom": "Distancias desde",
  "map.addressNotProvided": "Dirección no informada",
  "map.selectedNoAddress": "Dirección no informada para esta opción",
  "map.recenter": "Centrar",
  "map.recenterTitle": "Centrar el mapa",
  "map.clickToInteract": "Haz clic para interactuar con el mapa",
  "map.locationNotFound":
    "No encontramos esa dirección. Intenta buscar de otra forma.",
  "advantages.goToSlide": "Ir al destacado {{n}}",
  "visitDialog.title": "Agendar visitas",
  "visitDialog.description":
    "En este momento, se enviarían comunicaciones a los Partners seleccionados para verificar la disponibilidad de la visita a sus salas.",
  "poi.noDescription": "Sin descripción",

  // ── ComparisonSection ──
  "comparison.heading":
    "La forma *más inteligente* de contratar y administrar oficinas",

  // ── ComparisonSection (contenido "Ventaja Woba") ──
  "advantage.heading": "Tener una oficina *puede ser fácil*.",
  "advantage.subheadingPre": "Con Woba, tienes acceso a",
  "advantage.subheadingBold": "mucho más que una oficina",
  "advantage.subheadingPost":
    ": todo simple, práctico y sin burocracia. Y sin pagar nada extra por eso.",
  "advantage.cardsAriaLabel": "Ventajas Woba",
  "advantage.card1.title": "Mejor precio garantizado",
  "advantage.card1.descPre": "Con Woba,",
  "advantage.card1.descBold": "paga el mismo valor mensual",
  "advantage.card1.descPost":
    "que negociando directo con el partner, con hasta 15% de descuento negociado — y además llevas toda la red.",
  "advantage.card1.sealAlt": "Sello de mejor precio garantizado",
  "advantage.card1.tagline":
    "¿Encontraste algo mejor, dentro o fuera de la red? Nosotros lo cubrimos.",
  "advantage.card2.title": "Mucho más que una oficina",
  "advantage.card2.stat": "3.000+ espacios · 400+ ciudades",
  "advantage.card2.descPre": "Tu equipo con acceso a",
  "advantage.card2.descBold": "salas de reuniones y pases diarios de coworking",
  "advantage.card2.descPost":
    "en más de 3.000 espacios de la Red Woba en todo Brasil, sin pagar nada extra por eso.",
  "advantage.card2.badge": "Gana horas para usar en la red",
  "advantage.card3.title": "Tu equipo de facilities con superpoderes",
  "advantage.card3.descPre": "Un equipo de",
  "advantage.card3.descBold": "agentes de IA",
  "advantage.card3.descPost":
    "cuida tu operación — accesos, reservas, mantenimiento y contratos — todo resuelto en una sola factura, sin trámites.",
  "advantage.card4.title": "Oficina como servicio",
  "advantage.card4.descBold": "La flexibilidad que necesitas:",
  "advantage.card4.descPost":
    "aumenta, reduce o cambia de ciudad cuando el negocio lo pida. Sin multa, sin CAPEX, sin obra, sin contrato largo.",

  // ── CostBreakdownDrawer (badge "Entiende el precio" en la tarjeta) ──
  "costBreakdown.badge": "Entiende el precio",
  "costBreakdown.badgeWithScope": "Entiende el precio - {{scope}}",
  "costBreakdown.drawerTitle": "Todo eso, en una sola factura.",
  "costBreakdown.drawerSubtitle":
    "Tu equipo se enfoca en el negocio, no en el edificio.",
  "costBreakdown.compositionTitle": "Cómo funciona tu alquiler con Woba",
  "costBreakdown.compositionBodyPre":
    "Esta mensualidad se forma con los mismos 3 grupos de costos de un alquiler tradicional. Aquí, se convierten en un valor único, con una sola fecha de vencimiento, sin hoja de cálculo para conciliar.",
  "costBreakdown.compositionBodyBold": "Todo eso, en una sola factura.",
  "costBreakdown.totalLabel": "Total mensual",
  "costBreakdown.detailTitle": "Cómo funciona un alquiler tradicional",
  "costBreakdown.footnotePre":
    "En un alquiler tradicional, tu factura mensual se divide en 3 grandes grupos. Cada uno de esos grupos tiene varios ítems dentro, lo que suma {{count}} ítems para gestionar: {{count}} proveedores, {{count}} contratos, cada uno con su propia fecha de vencimiento y su propio punto de contacto. Al final, es tu equipo quien carga con ese peso cada mes.",
  "costBreakdown.category.rent": "Alquiler + Condominio + Impuesto predial",
  "costBreakdown.category.services": "Servicios Operativos",
  "costBreakdown.category.support": "Soporte y Depreciación",
  "costBreakdown.item.rentBase": "Alquiler base",
  "costBreakdown.item.condo": "Condominio",
  "costBreakdown.item.iptu": "Impuesto predial",
  "costBreakdown.item.energy": "Energía",
  "costBreakdown.item.staff": "Personal operativo",
  "costBreakdown.item.cleaning": "Limpieza",
  "costBreakdown.item.internet": "Internet / Telecom",
  "costBreakdown.item.maintenance": "Mantenimiento",
  "costBreakdown.item.water": "Agua",
  "costBreakdown.item.furniture": "Mobiliario (depreciación)",
  "costBreakdown.item.amenities": "Café / Amenities",
  "costBreakdown.item.insurance": "Seguros",
  "costBreakdown.item.admin": "Admin / Overhead",

  // ── CTASection / Footer ──
  "cta.tagline": "La forma más inteligente de contratar y administrar oficinas",
  "cta.contact": "Contacto",
  "cta.proposal": "Propuesta",
  "cta.validUntil": "Válida hasta:",
  "cta.validTBD": "Por definir",
  "cta.copyright": "© {{year}} Woba. Todos los derechos reservados.",

  // ── Logos marquee (ProposalCoverScreen footer) ──
  "logos.socialProof": "Haz como miles de empresas que confían en Woba",

  // ── VisitSchedulingStep ──
  "schedule.overline": "Agenda tu visita",
  "schedule.title": "Tú *eliges* cómo quieres conocerlo.",
  "schedule.subtitle":
    "Cuanto más a fondo vayas, más entiendes cómo funciona Woba en la práctica — y cuánto ahorra para {{companyName}}.",
  "schedule.subtitleNoCompany":
    "Cuanto más a fondo vayas, más entiendes cómo funciona Woba en la práctica — y cuánto puede ahorrarte.",
  "schedule.instruction":
    "Elige el día, el horario y el formato de la visita para cada espacio.",
  "schedule.dateTimeLabel": "Día y horario",
  "schedule.previousDates": "Días anteriores",
  "schedule.nextDates": "Próximos días",
  "schedule.modalityLabel": "Formato de la visita",
  "schedule.modalityInPerson": "Presencial",
  "schedule.modalityOnline": "Videollamada",
  "schedule.modalityTestDrive": "Test-drive",
  "schedule.modalityExplainerInPerson":
    "Real y de cerca. Conoce el espacio en persona, revisa la estructura de cerca y siente cómo sería la rutina de tu equipo ahí.",
  "schedule.modalityExplainerOnline":
    "Práctico e inmediato. Agenda una visita por videollamada para conocer la oficina y resolver dudas directo sobre el espacio.",
  "schedule.modalityExplainerTestDrive":
    "Completo, sin prisa. Usa la oficina por un día completo con tu equipo antes de decidir, sin costo.",
  "schedule.testDriveLearnMore": "Saber más",
  "schedule.testDriveDrawerTitle": "Test-drive de 1 día",
  "schedule.testDriveDrawerSubtitle":
    "Completo, sin prisa. Usa la oficina por un día completo con tu equipo antes de decidir, sin costo.*",
  "schedule.testDriveDrawerPhotoAlt":
    "Equipo trabajando en un espacio compartido de Woba",
  "schedule.testDriveHowItWorksTitle": "Cómo funciona",
  "schedule.testDriveStep1":
    "Elige la fecha disponible en el calendario — sin horario fijo, es el día completo.",
  "schedule.testDriveStep2":
    "Woba confirma la disponibilidad directo con el espacio.",
  "schedule.testDriveStep3":
    "Recibes la confirmación y llevas a tu equipo en el día acordado.",
  "schedule.testDriveStep4":
    "Decides con base en la experiencia real, no solo en fotos.",
  "schedule.testDriveBenefitsTitle": "Qué ganas",
  "schedule.testDriveBenefit1":
    "Prueba la rutina de tu equipo en el espacio antes de cerrar.",
  "schedule.testDriveBenefit2": "Sin costo — la visita es por cuenta de Woba.",
  "schedule.testDriveBenefit3":
    "Más seguridad para decidir, con datos reales del día a día.",
  "schedule.testDriveClosingNote":
    "* Sujeto a la confirmación del espacio, dentro de su horario de funcionamiento.",
  "schedule.testDriveNoteLabel": "¿Por qué quieres probar este espacio?",
  "schedule.testDriveNotePlaceholder":
    "Cuéntanos qué te llamó la atención de esta oficina y qué quieres confirmar con el test-drive",
  "schedule.testDriveSingleRoomError":
    "El test-drive es solo para 1 oficina a la vez. Elimina las demás para continuar.",
  "schedule.visitorCountLabel": "¿Cuántas personas van a la visita?",
  "schedule.visitorCountPlaceholder": "Ej: 4 personas",
  "schedule.valueBullet1":
    "Mejor precio garantizado — el mismo que negociando directo.",
  "schedule.valueBullet2": "Gana créditos para salas de reuniones y coworking.",
  "schedule.valueBullet3":
    "Flexibilidad para aumentar, reducir o cambiar de ciudad.",
  "schedule.visitTypeInPerson": "Visita presencial",
  "schedule.visitTypeOnline": "Visita por videollamada",
  "schedule.visitTypeTestDrive": "Test-drive (1 día)",
  "schedule.slotTakenHint": "Este horario ya fue elegido para otro espacio",
  "schedule.fewSlotsAlert":
    "Pocos horarios disponibles. Elige el tuyo y asegura tu visita.",
  "schedule.confirm": "Solicitar visita",
  "schedule.subStepAriaLabel": "Etapas del agendamiento",
  "schedule.subStepWhen": "Día y horario",
  "schedule.subStepData": "Tus datos",
  "schedule.dataOverline": "Ya casi",
  "schedule.dataTitle": "¿Quién va a *participar*?",
  "schedule.dataSubtitle":
    "Completa los datos de quién va a participar de la visita.",
  "schedule.hintIncomplete": "Elige día, horario y formato para cada espacio",
  "schedule.hintContactIncomplete": "Completa los campos para continuar",
  "schedule.fieldEmail": "Correo electrónico",
  "schedule.fieldEmailPlaceholder": "tu@correo.com",
  "schedule.fieldEmailError": "Ingresa un correo electrónico válido",
  "schedule.fieldWhatsappError": "Ingresa un número de teléfono válido",
  "schedule.addParticipant": "Incluir otro participante",
  "schedule.removeVisit": "Quitar este espacio de la visita",
  "schedule.removeParticipant": "Quitar participante",
  "schedule.participantLabel": "Participante {{n}}",
  "schedule.fieldName": "Nombre",
  "schedule.fieldNamePlaceholder": "Tu nombre",
  "schedule.fieldJobTitle": "Cargo",
  "schedule.fieldJobTitlePlaceholder": "Tu cargo",
  "schedule.fieldWhatsapp": "WhatsApp (recomendado)",
  "schedule.fieldWhatsappRequired": "WhatsApp",
  "schedule.fieldWhatsappPlaceholder": "(11) 90000-0000",
  "schedule.fieldCpf": "CPF (documento brasileño)",
  "schedule.fieldCpfPlaceholder": "000.000.000-00",
  "schedule.fieldCpfError": "Ingresa un CPF válido",
  "schedule.fieldCpfHint":
    "Se usa solo para liberar tu acceso al espacio el día del test-drive.",
  "privacy.consentPrefix":
    "Al enviar este formulario, declaras conocer nuestra",
  "privacy.consentLinkLabel": "Política de Privacidad",
  "privacy.consentSuffix":
    ". Tus datos se utilizarán para gestionar la solicitud realizada.",
  "schedule.successTitle": "¡Solicitud enviada!",
  "schedule.successSubtitle":
    "Tu solicitud fue enviada. Atento a tu correo: te avisamos en cuanto el partner confirme el horario.",
  "schedule.fullAddressNotice": "Dirección completa enviada al confirmar.",
  "schedule.postStep0Label": "Solicitud enviada",
  "schedule.postStep0Time": "Completado",
  "schedule.toBeConfirmed": "por confirmar",
  "schedule.postStep1Label": "Visitar oficina",
  "schedule.postStep2Label": "Propuesta final",
  "schedule.postStep3Label": "Firma y ocupa",
  "schedule.postStep1Time": "A partir de 48h",
  "schedule.postStep2Time": "48h después de la visita",
  "schedule.postStep3Time": "Hasta 15 días",
  "schedule.confirmationOverline": "Próximos pasos",
  "schedule.confirmationTitle": "Del interés a la operación, *en pocos días*.",
  "schedule.confirmationSubtitle":
    "Sigue lo que viene hasta que ocupes la nueva oficina.",
  "schedule.recapOverline": "Red Woba",
  "schedule.recapHeading": "Contratas una oficina.\nTe llevas un país entero.",
  "schedule.recapSubheading":
    "El 5% del valor de tu contrato se convierte en bono en créditos, para que tu equipo no quede atado a una única dirección. Puede usar salas de reunión, estaciones de trabajo y espacios de eventos en cualquier ciudad de Brasil.",
  "schedule.recapPricePre": "¿Y el precio?",
  "schedule.recapPriceBold": "Siempre el mismo que negociando directo",
  "schedule.recapPricePost": "— sin costo extra.",
  "schedule.recapStat1Value": "+3.000",
  "schedule.recapStat1Label": "oficinas flexibles",
  "schedule.recapStat2Value": "+4.200",
  "schedule.recapStat2Label": "salas de reunión",
  "schedule.recapStat3Value": "+1.000",
  "schedule.recapStat3Label": "espacios de eventos",
  "schedule.recapStat4Value": "+400",
  "schedule.recapStat4Label": "ciudades en LatAm",
  "schedule.recapType1Title": "Estaciones de Trabajo",
  "schedule.recapType1DescPre": "1 diaria en cualquiera de las",
  "schedule.recapType1DescCount": "+23.000",
  "schedule.recapType1DescPost": "estaciones",
  "schedule.recapType2Title": "Salas de Reunión",
  "schedule.recapType2DescPre": "1 hora en cualquiera de las",
  "schedule.recapType2DescCount": "+4.200",
  "schedule.recapType2DescPost": "salas",
  "schedule.recapType3Title": "Espacios de Eventos",
  "schedule.recapType3DescPre": "1 diaria en cualquiera de los",
  "schedule.recapType3DescCount": "+1.000",
  "schedule.recapType3DescPost": "espacios",
  "schedule.recapType4Title": "Oficinas Privadas",
  "schedule.recapType4DescPre": "1 mes en cualquiera de las",
  "schedule.recapType4DescCount": "+5.200",
  "schedule.recapType4DescPost": "oficinas",
  "schedule.recapCredits": "créditos",
  "schedule.recapCreditsPerPerson": "créditos/persona",
  "schedule.recapCreditsPerPosition": "créditos/posición",
  "schedule.successNotice":
    "Las condiciones de tu propuesta están garantizadas hasta {{date}}.",

  // ── Spaces (additional) ──
  "spaces.option": "Oficina Privada {{number}}",
  "spaces.ofTotal": "de {{total}}",

  // ── POI Categories ──
  "poi.restaurant": "Restaurantes",
  "poi.cafe": "Cafés",
  "poi.pharmacy": "Farmacias",
  "poi.transport": "Transporte",
  "poi.hotel": "Hoteles",
  "poi.parking": "Estacionamientos",

  // ── Chat (additional) ──
  "chat.defaultSpaceName": "este espacio",

  // ── CreditInfoDrawer ──
  "creditDrawer.viewDetails": "Ver detalles de los créditos",
  "creditDrawer.title": "Créditos incluidos en tu contratación",
  "creditDrawer.subtitle":
    "El 5% del valor de tu contrato se convierte en bono en créditos.",
  "creditDrawer.creditsLabel": "Créditos/mes incluidos*",
  "creditDrawer.creditsValue": "+{{credits}} créditos",
  "creditDrawer.creditsFootnote":
    "* Los créditos no son acumulativos: lo que no se use en el mes no pasa al mes siguiente.",
  "creditDrawer.howItWorksBody":
    "Tu equipo no queda atado a esta dirección: con los créditos incluidos, puede usar salas de reuniones, estaciones de trabajo y espacios de eventos en cualquier ciudad de Brasil — sin pagar nada extra por eso.",
  "creditDrawer.formatMeetingRoomTitle": "Salas de reuniones",
  "creditDrawer.formatMeetingRoomBody":
    "{{credits}} créditos equivalen a {{hours}}h de sala de reuniones para hasta 4 personas — el valor cambia según el tamaño de la sala.",
  "creditDrawer.formatCoworkingTitle": "Coworking (estaciones compartidas)",
  "creditDrawer.formatCoworkingBody":
    "{{credits}} créditos equivalen a {{credits}} días de uso en espacios compartidos.",
  "creditDrawer.formatPrivateOfficeTitle": "Oficinas privadas por día",
  "creditDrawer.formatPrivateOfficeBody":
    "Usa créditos para armar estructuras temporales para proyectos o equipos.",
  "creditDrawer.formatEventSpaceTitle":
    "Espacios para eventos y capacitaciones",
  "creditDrawer.formatEventSpaceBody":
    "Reserva salas más grandes y auditorios usando varios créditos.",
  "creditDrawer.quadroHoursLabel": "al mes",
  "creditDrawer.quadroDaysLabel": "días/mes",
  "creditDrawer.onRequest": "Bajo consulta",
  "creditDrawer.practiceTitle": "En la práctica",
  "creditDrawer.practiceDayToDayTitle": "Cómo se aplica esto en tu día a día",
  "creditDrawer.practiceBullet1": "Tu oficina deja de ser una única dirección",
  "creditDrawer.practiceBullet2":
    "Eliges el formato correcto para cada momento, sin necesidad de un contrato nuevo",
  "creditDrawer.practiceBullet3":
    "El crédito ya está incluido — no es un costo extra en tu factura",
  "creditDrawer.practiceCta":
    "En lugar de contratar más espacio, usas créditos cuando los necesitas.",

  // ── CreditInfoDrawer, variante "créditos extras" (ENT, PED-12653) ──
  "extraCreditDrawer.viewDetails": "Ver detalles de los créditos extras",
  "extraCreditDrawer.title": "Créditos extras en tu contratación",
  "extraCreditDrawer.subtitle":
    "Un paquete opcional de créditos, además de los incluidos, para usar en la red Woba.",
  "extraCreditDrawer.creditsLabel": "Créditos extras/mes*",
  "extraCreditDrawer.monthlyValueLabel": "Valor del paquete/mes",
  "extraCreditDrawer.howItWorksBody":
    "Además de los créditos incluidos, este paquete le da a tu equipo más créditos por mes para usar salas de reuniones, estaciones de trabajo y espacios de eventos en cualquier ciudad de Brasil — y entra en tu total mensual.",
  "extraCreditDrawer.practiceBullet3":
    "El paquete es opcional y entra en tu total mensual — sin contrato aparte",

  // ── RoomPriceCard (card de precio en el drawer de detalles, desktop) ──
  "priceCard.monthlyLabel": "Mensualidad de la oficina",
  "priceCard.monthlyLabelWithScope": "Mensualidad - {{scope}}",
  "priceCard.perPositionValue": "{{price}} / posición",

  // ── PriceGuaranteeModal ──
  "priceGuarantee.title": "Garantía de mejor precio",
  "priceGuarantee.subtext":
    "Mismo precio que el partner, hasta 15% de descuento",
  "priceGuarantee.bodyPre":
    "Negociamos directamente con el partner para garantizar el mejor precio de tu propuesta. Conseguimos ",
  "priceGuarantee.bodyBold": "hasta 15% de descuento",
  "priceGuarantee.bodyPost": " sobre el valor de la oficina.",
  "priceCard.creditsLabel": "Créditos incluidos",
  "priceCard.freeBadge": "gratis",
  "priceCard.creditsSubtext":
    "Equivalente a {{credits}} pases diarios en espacios compartidos/mes",
  "priceCard.flexCreditsLabel": "Créditos extras (opcional)",
  "priceCard.setupFeeLabel": "Setup Fee",
  "priceCard.setupFeeCaption": "Pagado una única vez",
  "priceCard.licenseFeeLabel": "License Fee",
  "priceCard.licenseFeeSemiannualCaption": "Pagado semestralmente",
  "priceCard.licenseFeeAnnualCaption": "Pagado anualmente",
  "priceCard.helpLink": "¿Todavía tienes dudas? Habla con nosotros",
  "priceCard.scheduleVisit": "Solicitar visita",
  "priceCard.makeProposal": "Hacer propuesta",

  // ── ProposalMakingStep (flujo "Hacer propuesta") ──
  "proposalMaking.hub.overline": "Haz tu propuesta",
  "proposalMaking.hub.headline": "Tú *decides* cómo avanzar.",
  "proposalMaking.hub.subtitle":
    "Revisa los valores y el resumen de la sala antes de elegir los próximos pasos para {{companyName}}.",
  "proposalMaking.hub.subtitleNoCompany":
    "Revisa los valores y el resumen de la sala antes de elegir los próximos pasos.",
  "proposalMaking.subStepDefine": "Condiciones de la propuesta",
  "proposalMaking.subStepAdjust": "Ajuste de precio y del espacio",
  "proposalMaking.subStepReview": "Identificación",
  "proposalMaking.subStepAriaLabel": "Pasos de la propuesta",
  "proposalMaking.choose.pickerLabel": "Elige una opción",
  "proposalMaking.choose.acceptLabel": "Aceptar condiciones",
  "proposalMaking.choose.acceptDescription":
    "Aceptar las condiciones actuales agiliza el análisis y pone tu propuesta por delante de las que aún se están negociando.",
  "proposalMaking.choose.negotiateLabel": "Negociar valor o pedir un ajuste",
  "proposalMaking.choose.negotiateDescription":
    "Propón un valor diferente o pide ajustes en el espacio — nuestro equipo evalúa y responde en hasta 2 días hábiles.",
  "proposalMaking.negotiate.overline": "Negociar valor",
  "proposalMaking.negotiate.headline": "Propón el valor *ideal* para ti.",
  "proposalMaking.negotiate.subtitle":
    "Ajusta el valor mensual propuesto y cuéntale a nuestro equipo por qué tiene sentido para ti — cuanto más cerca del valor actual, mayor la probabilidad de una aceptación rápida por parte del socio.",
  "proposalMaking.negotiate.subtitleNoPrice":
    "Cuéntale a nuestro equipo qué te gustaría proponer y por qué tiene sentido para ti — lo evaluará junto con el socio y te responderá.",
  "proposalMaking.negotiate.sectionTitle": "Ajuste de precio",
  "proposalMaking.negotiate.fieldLabel": "Tu propuesta",
  "proposalMaking.negotiate.fieldPlaceholder": "0",
  "proposalMaking.negotiate.goodProposalTitle": "¡Excelente propuesta!",
  "proposalMaking.negotiate.goodProposalDescription":
    "Un valor igual o mayor al actual tiene alta probabilidad de ser aceptado.",
  "proposalMaking.negotiate.moderateDiscountTitle":
    "Buena probabilidad de aceptación",
  "proposalMaking.negotiate.moderateDiscountDescription":
    "Descuentos de hasta el 15% tienen buenas probabilidades de ser aprobados por el socio.",
  "proposalMaking.negotiate.overLimitTitle": "Descuento por encima del límite",
  "proposalMaking.negotiate.overLimitDescription":
    "Podemos enviar propuestas con hasta {{percent}}% de descuento. Ajusta a {{minimum}} o más para continuar.",
  "proposalMaking.negotiate.overLimitHint":
    "Ajusta la propuesta a {{minimum}} o más para continuar",
  "proposalMaking.negotiate.discountBadge": "-{{percent}}%",
  "proposalMaking.negotiate.increaseBadge": "+{{percent}}%",
  "proposalMaking.negotiate.reasonLabel": "Cuéntanos el motivo de tu propuesta",
  "proposalMaking.negotiate.reasonPlaceholder":
    "Cuéntanos el motivo del ajuste de valor...",
  "proposalMaking.negotiate.flexCreditsLabel": "Créditos extras por mes",
  "proposalMaking.negotiate.flexCreditsUnit": "créditos",
  "proposalMaking.negotiate.flexCreditsHelper":
    "Equivale a {{value}}/mes. La negociación de arriba aplica solo a la mensualidad.",
  "proposalMaking.adjustmentsChoice.pickerLabel":
    "Ajuste de configuración del espacio",
  "proposalMaking.adjustmentsChoice.overline": "Ajustes en el espacio",
  "proposalMaking.adjustmentsChoice.headline": "¿Necesitas algún *ajuste*?",
  "proposalMaking.adjustmentsChoice.subtitle":
    "Puedes pedir retirar, instalar, reparar o sustituir un artículo.",
  "proposalMaking.adjustmentsChoice.noneLabel": "Sin ajustes",
  "proposalMaking.adjustmentsChoice.noneBadge":
    "Mayor probabilidad de ser aceptada",
  "proposalMaking.adjustmentsChoice.someLabel": "Con ajustes",
  "proposalMaking.adjustmentsChoice.someBadge":
    "Sujeto a evaluación del espacio",
  "proposalMaking.adjustmentType.overline": "Tipo de ajuste",
  "proposalMaking.adjustmentType.headline": "¿Cuál es el tipo de *ajuste*?",
  "proposalMaking.adjustmentType.subtitle":
    "Puedes agregar un ajuste a la vez.",
  "proposalMaking.adjustmentType.layout": "Layout",
  "proposalMaking.adjustmentType.layoutDescription":
    "Retirar o instalar un artículo, o cambiar/sustituir algo en el espacio.",
  "proposalMaking.adjustmentType.maintenance": "Mantenimiento o reparación",
  "proposalMaking.adjustmentType.maintenanceDescription":
    "Reparar o dar mantenimiento a algo que ya existe.",
  "proposalMaking.adjustmentType.positions": "Posiciones",
  "proposalMaking.adjustmentType.positionsDescription":
    "Aumentar o reducir el número de posiciones de la oficina.",
  "proposalMaking.adjustmentDescription.overline": "Detalle del ajuste",
  "proposalMaking.adjustmentDescription.headline":
    "Describe lo que *necesitas*.",
  "proposalMaking.adjustmentDescription.subtitle":
    "Cuantos más detalles, más rápido evaluamos tu pedido.",
  "proposalMaking.adjustmentDescription.placeholder":
    "Describe lo que necesitas...",
  "proposalMaking.adjustmentDescription.continueLabel": "Revisar",
  "proposalMaking.review.overline": "Ya casi",
  "proposalMaking.review.headline": "Revisa tu *propuesta*.",
  "proposalMaking.review.subtitle":
    "Confirma los valores y ajustes antes de enviarla a nuestro equipo.",
  "proposalMaking.review.bannerAccepted": "¡Esta es una excelente propuesta!",
  "proposalMaking.review.bannerNegotiating":
    "Tu propuesta será enviada para revisión de nuestro socio.",
  "proposalMaking.review.adjustmentsTitle": "Ajustes solicitados",
  "proposalMaking.review.addAdjustment": "Agregar ajuste",
  "proposalMaking.review.removeAdjustment": "Eliminar",
  "proposalMaking.review.identificationTitle": "Tus datos",
  "proposalMaking.review.occupancyDateLabel": "Fecha prevista de ocupación",
  "proposalMaking.review.contractTermLabel": "Plazo del contrato",
  "proposalMaking.review.contractTermOption.12": "12 meses",
  "proposalMaking.review.contractTermOption.24": "24 meses",
  "proposalMaking.review.contractTermOption.36": "36 meses",
  "proposalMaking.review.contractTermOption.48": "48 meses",
  "proposalMaking.review.contractTermOption.other": "Otro",
  "proposalMaking.review.contractTermCustomPlaceholder": "¿Cuántos meses?",
  "proposalMaking.review.submitLabel": "Confirmar y enviar",
  "proposalMaking.sent.overline": "Próximos pasos",
  "proposalMaking.sent.headline": "¡Propuesta enviada!",
  "proposalMaking.sent.subtitle": "Tu propuesta fue enviada al equipo Woba.",
  "proposalMaking.sent.notice": "Esta propuesta es válida hasta {{date}}.",
  "proposalMaking.sent.roomStatusLabel": "Propuesta enviada",
  "proposalMaking.sent.postStep0Label": "Propuesta enviada",
  "proposalMaking.sent.postStep0Time": "Completado",
  "proposalMaking.sent.postStep1Label": "En análisis",
  "proposalMaking.sent.postStep1Time": "Respuesta en hasta 2 días hábiles",
  "proposalMaking.sent.postStep2Label": "Aprobación",
  "proposalMaking.sent.postStep2Time": "Tras la respuesta del socio",
  "proposalMaking.sent.postStep3Label": "Firma y ocupa",
  "proposalMaking.sent.postStep3Time": "Hasta 15 días",

  // ── Briefing en profundidad desde la propuesta (PED-14146) ──
  "briefing.card.title": "Podemos ir más allá de estas opciones",
  "briefing.card.subtitle":
    "Cuéntanos lo que necesitas. Nosotros hacemos el resto:",
  "briefing.card.cta": "Contar lo que necesito",
  "briefing.card.smallTeam.bullet1": "Tú nos dices qué no sirvió",
  "briefing.card.smallTeam.bullet2": "Traemos nuevas opciones",
  "briefing.card.smallTeam.bullet3": "O ajustamos la sala elegida",
  "briefing.card.smallTeam.bullet4": "Sin empezar de cero",
  "briefing.card.largeTeam.bullet1": "Más opciones en nuestra red",
  "briefing.card.largeTeam.bullet2": "Adaptada antes de que entres",
  "briefing.card.largeTeam.bullet3": "O diseñada desde cero",
  "briefing.card.largeTeam.bullet4": "De la obra nos encargamos",

  "briefing.stepper.ariaLabel": "Etapas del formulario",
  "briefing.stepper.adjust": "Ajustes",
  "briefing.stepper.intent": "Qué buscas",
  "briefing.stepper.space": "El espacio",
  "briefing.stepper.contact": "Tus datos",
  "briefing.stage.overline": "Etapa {{number}} de {{total}}",
  "briefing.submitLabel": "Enviar",

  "briefing.adjust.title": "Vamos a *ajustar* la sala que te gustó",
  "briefing.adjust.subtitle":
    "Solo faltaron detalles de la sala. Dinos cuál te gustó y qué cambiarías.",
  "briefing.adjust.spaceLabel": "La sala que más te gustó",
  "briefing.adjust.spacePlaceholder":
    "Elige una de las opciones de la propuesta",
  "briefing.adjust.label":
    "¿Te gustó esta sala, pero harías algún ajuste para que sea ideal para tu equipo? Cuéntanos.",
  "briefing.adjust.groupStructural": "Cambios en la estructura",
  "briefing.adjust.structuralHelp":
    "Los cambios en la estructura convierten esto en un proyecto personalizado: el plazo y la inversión cambian, y un equipo específico asume tu pedido.",
  "briefing.adjust.simpleNotesLabel": "¿Quieres detallar esos ajustes?",
  "briefing.adjust.simpleNotesPlaceholder":
    "Ej.: cambiar las mesas por estaciones de 4 lugares y girar el layout hacia la ventana.",
  "briefing.adjust.structuralNotesLabel": "¿Quieres detallar esos cambios?",
  "briefing.adjust.structuralNotesPlaceholder":
    "Ej.: cerrar la sala del fondo con una mampara de vidrio para que sea sala de reunión.",
  "briefing.adjust.notesLabel": "¿Algo más que cambiarías?",
  "briefing.adjust.notesPlaceholder":
    "Ej.: cambiaría las mesas por estaciones de 4 lugares.",

  "briefing.stepper.feedback": "Las opciones",
  "briefing.feedback.title": "¿Qué te parecieron las *opciones*?",
  "briefing.feedback.subtitle":
    "Dinos qué funcionó y qué no en cada una. Es lo que hace que la próxima búsqueda sea más certera.",
  "briefing.feedback.cardRooms": "Las opciones que viste",
  "briefing.feedback.help":
    "Marca el pulgar en al menos una opción. Si ya evaluaste alguna en el listado, aparece aquí marcada.",
  "briefing.feedback.likedLabel": "Lo que te gustó",
  "briefing.feedback.dislikedLabel": "Lo que no sirvió",
  "briefing.feedback.missedLabel": "Qué faltó",
  "briefing.feedback.missedHelp":
    "Cuéntanos con tus palabras qué te impidió seguir con alguna de ellas.",
  "briefing.feedback.missedPlaceholder":
    "Ej.: la zona está bien, pero todas quedaron lejos del metro.",
  "briefing.feedback.needLabel": "Qué necesita tener",
  "briefing.feedback.needHelp":
    "Lo que la próxima opción no puede dejar de tener. Un ítem por línea.",
  "briefing.feedback.needPlaceholder":
    "Ej.: sala de reunión para 10 personas\nEj.: espacio para que el equipo de ventas haga llamadas",
  "briefing.feedback.cardSearch": "La nueva búsqueda",
  "briefing.section.feedback": "Nueva búsqueda",
  "briefing.hint.feedback":
    "Evalúa al menos una opción y completa los campos marcados con *.",

  "briefing.intent.title": "¿Qué *buscas*?",
  "briefing.intent.subtitle":
    "Tu respuesta define qué te mostramos y quién de nuestro equipo asume el pedido.",
  "briefing.intent.label": "Elige una opción",
  "briefing.intent.option.readyOffices.title":
    "Otras oficinas listas para ocupar",
  "briefing.intent.option.readyOffices.description":
    "Buscamos nuevas opciones en el inventario Woba, en el mismo formato de las que viste.",
  "briefing.intent.option.customizable.title":
    "Personalizar una de las oficinas que viste",
  "briefing.intent.option.customizable.description":
    "Eliges una de las opciones de la propuesta y dices qué cambiarías: layout, mobiliario, marca o la propia estructura.",
  "briefing.intent.option.tailorMade.title":
    "Un espacio exclusivo, con obra a medida",
  "briefing.intent.option.tailorMade.description":
    "Encontramos el inmueble y diseñamos la oficina desde cero para tu equipo. Mayor plazo e inversión.",
  // Aviso de preço sob Personalizável e Sob Medida (pedido do Growth).
  "briefing.priceNotice.customizable":
    "Una oficina personalizada cuesta entre un 20% y un 40% más que una oficina lista para ocupar.",
  "briefing.priceNotice.tailorMade":
    "Una oficina a medida cuesta entre un 20% y un 40% más que una oficina lista para ocupar.",
  "briefing.priceNotice.accept": "Lo entiendo y quiero continuar",

  "briefing.space.tailorMade.title": "¿Cómo es el *espacio* que imaginas?",
  "briefing.space.tailorMade.subtitle":
    "Ya tenemos la zona y el presupuesto de tu propuesta. Ahora cuéntanos cómo tiene que ser el inmueble — es a partir de aquí que se diseña el proyecto.",
  "briefing.space.cardBuilding": "El inmueble",

  "briefing.field.region.label": "Ciudad y zona / barrio deseado",
  "briefing.field.region.placeholder":
    "Ej.: São Paulo — Pinheiros o Faria Lima",
  "briefing.field.region.help":
    "Empezamos por la zona de tu propuesta. Cámbiala por donde quieres buscar ahora.",
  "briefing.field.people.label": "Número de personas",
  "briefing.field.people.placeholder": "Ej.: 24",
  "briefing.field.people.help":
    "Viene de tu propuesta. Cámbialo si el número es otro.",
  "briefing.field.deadline.label": "Plazo para entrar",
  "briefing.field.budget.label": "Presupuesto mensual",
  "briefing.field.budget.placeholder": "Ej.: hasta R$ 30.000 por mes",
  "briefing.field.budget.help":
    "Viene de tu propuesta. Opcional, pero ayuda a que solo traigamos lo que cabe.",
  "briefing.field.mustHave.label": "Lo que no puede faltar",
  "briefing.field.mustHave.placeholder":
    "Ej.: sala de reunión para 10 personas\nEj.: espacio para que el equipo de ventas haga llamadas",
  "briefing.field.mustHave.help": "Un ítem por línea.",
  "briefing.field.sizeM2.label": "Metraje aproximado (m²)",
  "briefing.field.sizeM2.placeholder": "Ej.: 300",
  "briefing.field.buildingType.label": "Tipo de inmueble",
  "briefing.field.standard.label": "Estándar deseado",
  "briefing.field.nearby.label": "Comodidades básicas obligatorias",
  "briefing.field.priorities.label": "Tus tres mayores prioridades",
  "briefing.field.priorities.help":
    "En orden: lo que miras primero cuando comparas dos espacios.",
  "briefing.field.priority1.placeholder": "1.ª prioridad",
  "briefing.field.priority2.placeholder": "2.ª prioridad",
  "briefing.field.priority3.placeholder": "3.ª prioridad",
  "briefing.field.adjustments.label": "Cambios que harías en el espacio",
  "briefing.field.likedSpace.label": "Sala que le gustó al cliente",
  "briefing.field.missedNotes.label": "Detalles de lo que faltó",
  "briefing.field.adjustmentNotes.label": "Detalles de los cambios",
  "briefing.field.intent.label": "Qué busca",
  "briefing.field.name.label": "Nombre",
  "briefing.field.jobTitle.label": "Cargo",
  "briefing.field.email.label": "Correo electrónico",
  "briefing.field.whatsapp.label": "WhatsApp",

  "briefing.option.adjustments.furnitureLayout":
    "Cambiar el layout de los muebles",
  "briefing.option.adjustments.furnitureSwap": "Cambiar o agregar mobiliario",
  "briefing.option.adjustments.deskCount":
    "Más o menos puestos en la misma sala",
  "briefing.option.adjustments.maintenance":
    "Reparar o dar mantenimiento a algo que ya existe",
  "briefing.option.adjustments.storage": "Armarios / espacio de guardado",
  "briefing.option.adjustments.branding":
    "Personalizar con la marca de la empresa",
  "briefing.option.adjustments.otherRoomSameBuilding":
    "Ver otra sala en el mismo edificio",
  "briefing.option.adjustments.walls": "Derribar o levantar una pared",
  "briefing.option.adjustments.internalMeetingRoom":
    "Crear sala de reunión o cabina en el espacio",
  "briefing.option.adjustments.flooringCeiling": "Cambiar piso o cielorraso",
  "briefing.option.adjustments.installations":
    "Modificar aire acondicionado, electricidad o plomería",
  "briefing.option.adjustments.mergeRooms": "Unir dos salas",
  "briefing.option.intent.readyOffices": "Otras oficinas listas para ocupar",
  "briefing.option.intent.customizable":
    "Personalizar una de las oficinas que vio",
  "briefing.option.intent.tailorMade":
    "Un espacio exclusivo, con obra a medida",
  "briefing.option.deadline.upTo30Days": "Hasta 30 días",
  "briefing.option.deadline.upTo3Months": "En 1 a 3 meses",
  "briefing.option.deadline.upTo6Months": "En 3 a 6 meses",
  "briefing.option.deadline.over6Months": "Más de 6 meses",
  "briefing.option.deadline.noDate": "Todavía no tengo fecha",
  "briefing.option.buildingType.corporateSlab":
    "Planta en edificio corporativo",
  "briefing.option.buildingType.streetBuilding": "Casa o inmueble a la calle",
  "briefing.option.buildingType.either": "Da igual",
  "briefing.option.standard.premium": "Alto estándar (AAA)",
  "briefing.option.standard.intermediate": "Intermedio",
  "briefing.option.standard.simple": "Más sencillo",
  "briefing.option.nearby.kitchen": "Cocina o comedor",
  "briefing.option.nearby.buildingReception": "Recepción en el edificio",
  "briefing.option.nearby.publicTransport": "Transporte público cerca",
  "briefing.option.nearby.restaurants": "Restaurantes cerca",
  "briefing.option.nearby.parking": "Estacionamiento",

  "briefing.stepper.routine": "Equipo y prioridades",
  "briefing.routine.title": "¿Cómo va a *usar* el espacio tu equipo?",
  "briefing.routine.subtitle":
    "El día a día, lo que no puede faltar y referencias que nos ayudan a acertar más rápido.",
  "briefing.routine.cardMeetingRooms": "Salas de reunión",
  "briefing.routine.cardPriorities": "Qué es prioridad",
  "briefing.routine.cardReferences": "Referencias",
  "briefing.space.cardSize": "Tamaño y plazo",
  "briefing.field.investment.label":
    "Para tener el espacio a tu manera, invertirías:",
  "briefing.option.investment.sameValue": "Lo mismo que la propuesta",
  "briefing.option.investment.aLittleMore": "Un poco más",
  "briefing.option.investment.whateverItTakes":
    "Lo que haga falta para que el espacio quede bien",
  "briefing.field.nearbyScope.label":
    "¿Podemos buscar también en zonas cercanas?",
  "briefing.option.nearbyScope.sameRegion": "Solo en la misma zona",
  "briefing.option.nearbyScope.sameAndNearby":
    "En la misma zona y en zonas cercanas",
  "briefing.field.parking.label": "Estacionamiento",
  "briefing.field.parking.placeholder": "¿Cuántas plazas necesitas? Ej.: 5",
  "briefing.field.meetingRooms.placeholder": "¿Cuántas?",
  "briefing.field.meetingRoomsSmall.label": "Pequeñas (hasta 4 personas)",
  "briefing.field.meetingRoomsMedium.label": "Medianas (6 a 8 personas)",
  "briefing.field.meetingRoomsLarge.label": "Grandes (10 o más)",
  "briefing.field.niceToHave.label": "Sería bueno tener",
  "briefing.field.niceToHave.placeholder":
    "Ej.: bicicletero, gimnasio en el edificio, terraza",
  "briefing.field.likedReference.label":
    "¿Alguna oficina o edificio que hayas visto y te haya gustado? Cuéntanos qué pesó: dirección, edificio, planta, precio.",
  "briefing.field.likedReference.placeholder":
    "Ej.: el edificio X en Faria Lima, por la ubicación y la planta",
  "briefing.field.discardedReference.label":
    "¿Alguno que descartaste? ¿Por qué?",
  "briefing.field.discardedReference.placeholder":
    "Ej.: el edificio Y, porque no tenía estacionamiento",
  "briefing.section.routine": "Día a día, prioridades y referencias",
  "briefing.hint.routine":
    "Marca al menos una comodidad y completa la 1.ª prioridad.",

  "briefing.contact.title": "Tus *datos*",
  "briefing.contact.subtitle": "A quién debemos responder con las opciones.",

  "briefing.hint.adjust": "Elige la sala y al menos un cambio.",
  "briefing.hint.intent": "Elige una de las opciones.",
  "briefing.hint.priceNotice": "Confirma el aviso de precio para continuar.",
  "briefing.hint.space":
    "Indica el plazo y el estándar — o, si vas a ajustar, la sala y los cambios.",
  "briefing.hint.contact": "Completa tus datos para enviar.",

  "briefing.section.adjust": "Ajustes en la sala elegida",
  "briefing.section.intent": "Qué buscas",
  "briefing.section.space": "El espacio deseado",
  "briefing.section.contact": "Tus datos",

  "briefing.sent.overline": "Pedido enviado",
  "briefing.sent.headline": "¡Respuestas *enviadas*!",
  "briefing.sent.subtitleReadySearch":
    "Bruna ya va a buscar nuevas opciones en nuestro inventario, con tus evaluaciones en mano, y te presentará lo que encuentre.",
  "briefing.sent.subtitleReady":
    "Nuestro equipo buscará nuevas opciones con base en lo que respondiste y vuelve con las alternativas.",
  "briefing.sent.subtitleCustomizable":
    "Tu pedido tiene cambios en el espacio, así que fue al equipo de oficinas personalizadas. Ellos vuelven con lo que es posible hacer, el plazo y la inversión.",
  "briefing.sent.subtitleTailorMade":
    "Nuestro equipo usará tus respuestas para buscar el inmueble y diseñar la oficina de tu equipo. Recibirás una propuesta a medida.",
  "briefing.sent.summaryLabel": "Resumen de lo que enviaste",
  "briefing.sent.postStep0Label": "Respuestas enviadas",
  "briefing.sent.postStep0Time": "Completado",
  "briefing.sent.postStep1LabelReady": "Búsqueda de nuevas opciones",
  "briefing.sent.postStep1TimeReady": "En hasta 2 días hábiles",
  "briefing.sent.postStep1LabelBuild": "Estudio de tu pedido",
  "briefing.sent.postStep1TimeBuild": "En hasta 7 días",
  "briefing.sent.postStep2LabelReady": "Nuevas opciones en la propuesta",
  "briefing.sent.postStep2TimeReady": "Después de la búsqueda",
  "briefing.sent.postStep2LabelBuild": "Propuesta con plazo e inversión",
  "briefing.sent.postStep2TimeBuild": "Después del estudio",
  "briefing.sent.postStep3Label": "Firma y ocupa",
  "briefing.sent.postStep3TimeReady": "Hasta 15 días",
  "briefing.sent.postStep3TimeBuild": "Plazo a acordar",

  // ── Rating (evaluación de la recomendación) ──
  "rating.prompt": "¿Acertamos en la elección?",
  "rating.drawerPrompt": "¿Acertamos en la elección de esta oficina?",
  "rating.thumbUp": "Me gustó esta oficina",
  "rating.thumbDown": "No me gustó esta oficina",
  "rating.modalTitlePositive": "¡Bien! Acertamos en la recomendación",
  "rating.modalTitleNegative": "Todavía no acertamos en la recomendación",
  "rating.modalSupportPositive":
    "Tu evaluación nos ayuda a entender qué te funcionó.",
  "rating.modalSupportNegative":
    "Tenemos más opciones para ti. Tu evaluación nos ayuda a traer las más acertadas.",
  "rating.questionNegative": "¿Qué no encajó?",
  "rating.questionPositive": "¿Qué fue lo que más te gustó?",
  "rating.subReasonsLabel": "¿Qué falta?",
  "rating.commentLabel": "¿Quieres contarnos más? (opcional)",
  "rating.commentPlaceholder": "Escribe aquí...",
  "rating.submit": "Enviar evaluación",
  "rating.confirmationTitle": "¡Recibido!",
  "rating.confirmationBody":
    "Usaremos tu evaluación para mejorar las próximas sugerencias.",
  "rating.close": "Cerrar",
  "rating.reason.photos": "Las fotos no ayudan a entender la oficina",
  "rating.reason.capacity":
    "No tiene capacidad para el número de personas que necesito",
  "rating.reason.location": "La zona no me funciona",
  "rating.reason.price": "No vale el precio pedido",
  "rating.reason.layoutCondition":
    "El layout o el estado de la oficina no me agradó",
  "rating.reason.missingRequirements": "Falta algo que necesito",
  "rating.reason.capacityFit": "Le queda bien a mi equipo",
  "rating.reason.locationGreat": "La zona es excelente para mí",
  "rating.reason.visualAppeal": "Me gustó el aspecto de la oficina",
  "rating.reason.amenitiesComplete": "Tiene todo lo que necesito",
  "rating.reason.priceValue": "Buena relación calidad-precio",
  "rating.subReason.amenities": "Amenidades",
  "rating.subReason.equipment": "Equipamiento",
  "rating.subReason.accessibility": "Accesibilidad",
  "rating.subReason.other": "Otras características",
};
